import * as React from 'react';
import { useEffect } from 'react';
import { PaccVideoDetailResultVO, PaccVideoResultVO } from '@domain/vo/phs/pacc/results/PaccVideoResultVO';
import { VideoType, videoTypeStringMap } from '@domain/constant/videoType';
import { videoQuestionTypeStringMap } from '@domain/constant/videoQuestionType';
import classNames from 'classnames/bind';
import styles from './v4TabList.module.scss';

const cx = classNames.bind(styles);

interface V4TabListProps {
  videoResult: PaccVideoResultVO;
  videoDetailTab: PaccVideoDetailResultVO | null;
  setVideoDetailTab: (videoDetailTab: PaccVideoDetailResultVO | null) => void;
  openedSubTab: PaccVideoDetailResultVO | null;
  setOpenedSubTab: (videoDetailTab: PaccVideoDetailResultVO | null) => void;
}

const V4TabList = ({
  videoResult,
  videoDetailTab,
  setVideoDetailTab,
  openedSubTab,
  setOpenedSubTab,
}: V4TabListProps) => {
  const accVideoDetailMap = new Map<VideoType, PaccVideoDetailResultVO[]>();
  const { details } = videoResult;

  details?.forEach((detail) => {
    const { type } = detail;
    accVideoDetailMap?.set(type, [...(accVideoDetailMap?.get(type) || []), detail]);
  });

  const accVideoDetailResultList = accVideoDetailMap?.get('VIDEO_BASIC_EXPERIENCE');

  useEffect(() => {
    initTab();
  }, []);

  const initTab = () => {
    if (!accVideoDetailResultList) return;
    const accVideoDetailResult = accVideoDetailResultList[0];
    setVideoDetailTab(accVideoDetailResult);
    setOpenedSubTab(accVideoDetailResult);
  };

  const getTabListEl = (videoType: VideoType) => {
    const accVideoDetailResultList = accVideoDetailMap?.get(videoType);
    if (!accVideoDetailResultList || accVideoDetailResultList.length === 0) return null;

    // 경험 기반 질문, 가치관 질문은 항상 꼬리 질문으로 존재
    const hasSubTab =
      accVideoDetailResultList.length > 1 ||
      videoType === 'VIDEO_BASIC_EXPERIENCE' ||
      videoType === 'VIDEO_BASIC_VALUES';

    const accVideoDetailResult = accVideoDetailResultList[0];
    const { voiceErrorYn, videoErrorYn } = accVideoDetailResult;
    const title = `${videoTypeStringMap[videoType]} 면접`;
    const isWarning = !hasSubTab && (voiceErrorYn || videoErrorYn);

    const getIsOnTab = () => {
      if (hasSubTab) return openedSubTab === accVideoDetailResult;
      else return videoDetailTab === accVideoDetailResult;
    };

    const handleClick = () => {
      if (hasSubTab) {
        if (openedSubTab === accVideoDetailResult) setOpenedSubTab(null);
        else setOpenedSubTab(accVideoDetailResult);
      } else setVideoDetailTab(accVideoDetailResult);
    };

    const subTabListEl = accVideoDetailResultList?.map((videoDetail, index) => {
      const { type, questionNum, voiceErrorYn, questionType, videoErrorYn } = videoDetail;
      const isOnTab = videoDetailTab === videoDetail;
      const isWarning = voiceErrorYn || videoErrorYn;
      const handleClick = () => setVideoDetailTab(videoDetail);
      const getTitle = () => {
        if (questionType === 'MAIN') return `${videoTypeStringMap[type]} 질문 ${questionNum}`;
        else return `관련 질문 ${questionNum}-${videoQuestionTypeStringMap[questionType]}`;
      };

      return (
        <a key={index} className={cx('btnSubTab', { on: isOnTab })} onClick={handleClick} role="button">
          <span className={cx('tabText', { warning: isWarning })}>{getTitle()}</span>
        </a>
      );
    });

    return (
      <div className={cx('tabWrap')}>
        <a className={cx('btnTab', { hasSubTab: hasSubTab }, { on: getIsOnTab() })} onClick={handleClick} role="button">
          <span className={cx('tabText', { warning: isWarning })}>{title}</span>
        </a>

        {/* [D] 서브탭 영역 */}
        {hasSubTab && <div className={cx('subTabArea')}>{subTabListEl}</div>}
      </div>
    );
  };

  if (!accVideoDetailMap) return null;

  return <div className={cx('tabListArea')}>{getTabListEl('VIDEO_BASIC_EXPERIENCE')}</div>;
};

export default V4TabList;
